import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
    AccessCodeService,
    AuthService,
    NamedRoutes,
    ToastService,
    TokenService,
    RouterService,
    URLService,
    ThemeService,
    TranslationService,
} from '@raptor-fe/services';
import { AccessTokenDetails, CodeLoginData, ReportLinkVerification } from '@raptor-fe/core';
import { catchError, firstValueFrom, switchMap, timeout } from 'rxjs';

export interface CodeLoginState {
    accessCodeId: string;
    loading: boolean;
    brandingResolved: boolean;
    verification: ReportLinkVerification;
}

/**
 * How long to wait for branding before showing the page anyway. Long enough for a normal round
 * trip to blob storage, short enough that a customer never sits on a spinner wondering whether the
 * link is broken. Unbranded beats blank.
 */
const BRANDING_TIMEOUT_MS = 3000;

/**
 * The message to show when a login attempt is refused.
 *
 * The server answers an unknown link, a wrong registration and a wrong code identically and on
 * purpose, so its own message cannot name what was wrong -- it says "Invalid code" even when a
 * registration was submitted. The client can be specific, because it knows which credential it just
 * sent, and naming it back to the customer discloses nothing they did not type themselves.
 *
 * Anything that is not a refusal, an expired link most of all, keeps the server's message: those
 * are the cases where the server knows something useful that the client does not.
 */
function refusalMessage(result: { status?: number; error?: { message?: string } }, submittedRegistration: boolean): string {
    const wasRefused = result?.status === 401 || result?.status === 403;

    if (!wasRefused) {
        return result?.error?.message ?? 'We could not open your report. Please try again.';
    }

    return submittedRegistration
        ? "That registration does not match this report. Check it and try again."
        : "That verification code is not right. Check it and try again.";
}

export const CodeLoginStore = signalStore(
    withState<CodeLoginState>({
        accessCodeId: '',
        loading: false,
        brandingResolved: false,
        verification: ReportLinkVerification.None,
    }),
    withMethods(
        (
            store,
            accessCodeService = inject(AccessCodeService),
            toastService = inject(ToastService),
            tokenService = inject(TokenService),
            authService = inject(AuthService),
            routerService = inject(RouterService),
            urlService = inject(URLService),
            themeService = inject(ThemeService),
            translationService = inject(TranslationService),
        ) => ({
            async setAccessCodeId(id: string): Promise<void> {
                patchState(store, { accessCodeId: id });
            },

            /**
             * Loads which credential to ask for and applies the retailer's branding.
             *
             * A customer opening a shared report link should see the business that inspected their
             * vehicle, not whichever product happens to host the page. Failure is deliberately
             * silent and leaves the mode at None, which makes the page ask for a code: an error toast
             * on a page the customer has not interacted with yet would be worse than no logo, and
             * every issue-time fallback leaves the code in the link, so asking for a code cannot open
             * anything the server would have refused.
             */
            async loadBranding(): Promise<void> {
                if (!store.accessCodeId()) {
                    patchState(store, { brandingResolved: true });
                    return;
                }

                await firstValueFrom(
                    accessCodeService.getLoginContext(store.accessCodeId()).pipe(
                        timeout(BRANDING_TIMEOUT_MS),
                        switchMap(async (context) => {
                            themeService.applyBranding(context ?? {});
                            patchState(store, {
                                verification: context?.verification ?? ReportLinkVerification.None,
                            });
                        }),
                        catchError(async () => {
                            /* Unbranded, asking for a code, is an acceptable fallback. */
                        }),
                    ),
                );

                patchState(store, { brandingResolved: true });
            },

            // Send the access code to the customer
            async sendAccessCode(): Promise<void> {
                patchState(store, { loading: true });

                await firstValueFrom(
                    accessCodeService.sendAccessCode(store.accessCodeId()).pipe(
                        switchMap(async () => {
                            await toastService.showSuccess(
                                'Your verification code was sent. Please check your email or phone and enter it into the Verification box to continue.',
                            );
                        }),
                        catchError(async (error: { error: Error }) => {
                            patchState(store, { loading: false });
                            await toastService.showError(error?.error?.message ?? 'Error sending access code.');
                        }),
                    ),
                );
            },

            /**
             * Opens the report with whichever credential the link calls for: the access code when
             * the link carries one, or the vehicle registration when the tenant requires the
             * challenge and the code was left out of the link.
             *
             * Returns whether the report opened. A refusal is already toasted here; the return
             * value exists so the page can decide what to render next, because a link can carry a
             * code the server no longer accepts once its tenant has switched to Registration mode.
             *
             * The dictionary is fetched before navigating because a customer on a shared link has no
             * sign-in step that would load it, and the report must show the tenant's wording.
             */
            async login(credential: { code?: string; registration?: string }): Promise<boolean> {
                tokenService.removeToken();
                tokenService.removeRefreshToken();

                const codeLoginData: CodeLoginData = {
                    code: credential.code,
                    id: store.accessCodeId(),
                    registration: credential.registration,
                };

                return await firstValueFrom(authService.codeLogin(codeLoginData)).then(
                    async (accessTokenDetails: AccessTokenDetails) => {
                        const openToken = authService.getDecodedAccessToken(accessTokenDetails.access_token as string);
                        const accessCodeValue = JSON.parse(openToken?.access_code_value ?? '{}');
                        const { tenantId, branchId } = accessCodeValue;

                        await firstValueFrom(translationService.refreshDictionary(), { defaultValue: null });

                        if (openToken?.access_code_type === 'DVIConsolidatedReport') {
                            urlService.saveURL(openToken?.external_url as string);

                            const route = accessCodeValue.workOrderId
                                ? NamedRoutes.dviConsolidatedReport({ tenantId, branchId, workOrderId: accessCodeValue.workOrderId })
                                : NamedRoutes.dviConsolidatedReport({ tenantId, branchId, quoteId: accessCodeValue.quoteId });

                            routerService.navigateAndReplaceUrl(route);
                        } else if (openToken?.access_code_type === 'DVIInspectionReport') {
                            urlService.saveURL(openToken?.external_url as string);

                            routerService.navigateAndReplaceUrl(
                                NamedRoutes.dviInspectionDownload({ tenantId, branchId, inspectionId: accessCodeValue.inspectionId }),
                            );
                        }

                        return true;
                    },
                    (result) => {
                        toastService.showError(refusalMessage(result, !!credential.registration));

                        return false;
                    },
                );
            },
        }),
    ),
);
